import React from "react";
import { Link, useLocation } from "react-router-dom";
import { HiOutlineMenuAlt3 } from "react-icons/hi";
import {
  FiActivity,
  FiBell,
  FiBriefcase,
  FiCalendar,
  FiCheckSquare,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiBarChart2,
  FiClock,
  FiFileText,
  FiGitBranch,
  FiHome,
  FiMapPin,
  FiRefreshCw,
  FiUnlock,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import {
  canAccessQtMenus,
  canApproveTravelExpense,
  canCreateTravelExpense,
  isAdmin,
} from "../utils/buttonVisibility";
import { fetchTimesheetCapabilities } from "../services/timesheetApi";
import { canViewApprovedTimesheets } from "../utils/timesheetApprovalAccess";

const Sidebar = ({
  isSidebarOpen = false,
  setIsSidebarOpen = () => {},
  desktopCollapsed = false,
  setDesktopCollapsed = () => {},
}) => {
  const [isFlowMenuOpen, setIsFlowMenuOpen] = React.useState(false);
  const [isTravelMenuOpen, setIsTravelMenuOpen] = React.useState(false);
  const [isTimesheetMenuOpen, setIsTimesheetMenuOpen] = React.useState(false);
  const [timesheetCapabilities, setTimesheetCapabilities] = React.useState({
    canViewDashboard: false,
    canApproveTimesheet: false,
  });
  const location = useLocation();
  const showQtMenus = canAccessQtMenus();
  const showTravelApproveMenu = canApproveTravelExpense();
  const showTimesheetApprovalMenu =
    timesheetCapabilities.canApproveTimesheet || canViewApprovedTimesheets();

  React.useEffect(() => {
    if (location.pathname.startsWith("/travel-expense")) {
      setIsTravelMenuOpen(true);
    }

    if (location.pathname.startsWith("/timesheets")) {
      setIsTimesheetMenuOpen(true);
    }

    if (
      location.pathname.startsWith("/approve-flow") ||
      location.pathname.startsWith("/manage-flows") ||
      location.pathname.startsWith("/repair-approval-flow") ||
      location.pathname.startsWith("/create-team")
    ) {
      setIsFlowMenuOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");

    if (!token) return undefined;

    fetchTimesheetCapabilities()
      .then((capabilities) => {
        if (active) setTimesheetCapabilities(capabilities);
      })
      .catch(() => {
        if (active) {
          setTimesheetCapabilities({
            canViewDashboard: false,
            canApproveTimesheet: false,
          });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const safeSetOpen = (value) => {
    if (typeof setIsSidebarOpen === "function") {
      setIsSidebarOpen(value);
    }
  };

  const toggleSidebar = () => safeSetOpen(!isSidebarOpen);
  const closeMobileAfterClick = () => setTimeout(() => safeSetOpen(false), 80);

  const NavItem = ({ to, icon, label, active, onClick }) => (
    <Link
      to={to}
      onClick={onClick}
      className={[
        "group relative flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200",
        active
          ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
          : "text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-300",
      ].join(" ")}
    >
      <span
        className={[
          "absolute left-0 top-1 bottom-1 w-1 rounded-r transition-all",
          active
            ? "bg-indigo-500 dark:bg-indigo-500 shadow-[0_0_6px_#6366f1]"
            : "bg-transparent group-hover:bg-indigo-200 dark:group-hover:bg-indigo-500",
        ].join(" ")}
      />
      <span className="text-[18px] shrink-0">{icon}</span>
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );

  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-50 bg-white/90 dark:bg-gray-900/80 backdrop-blur-md p-2 rounded-lg shadow-lg"
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        <HiOutlineMenuAlt3
          size={22}
          className="text-gray-800 dark:text-gray-200"
        />
      </button>

      {isSidebarOpen && (
        <div
          onClick={() => safeSetOpen(false)}
          className="fixed inset-0 bg-black/40 z-[60] md:hidden"
        />
      )}

      <aside
        className={[
          "fixed top-0 left-0 h-full w-64 z-[70] transition-transform",
          "bg-white/70 dark:bg-gray-900 backdrop-blur-xl shadow-xl border-r border-gray-200 dark:border-gray-800",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full",
          desktopCollapsed ? "md:-translate-x-full" : "md:translate-x-0",
        ].join(" ")}
      >
        <button
          onClick={() =>
            typeof setDesktopCollapsed === "function" &&
            setDesktopCollapsed(!desktopCollapsed)
          }
          className="hidden md:flex absolute -right-3 top-16 h-9 w-9 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-md text-gray-700 dark:text-gray-200 hover:bg-indigo-500 hover:text-black transition-colors duration-200 z-[80]"
          title={desktopCollapsed ? "Expand" : "Collapse"}
          aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {desktopCollapsed ? (
            <FiChevronRight size={20} />
          ) : (
            <FiChevronLeft size={20} />
          )}
        </button>

        <div className="h-14 px-4 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3">
          <img
            src="/optx-icon.png"
            alt="OPTX"
            className="w-8 h-8 object-contain drop-shadow-[0_0_3px_#6366f1]"
          />
          <span className="text-sm font-semibold tracking-wide text-gray-800 dark:text-indigo-500 transition-colors duration-300">
            OPTX
          </span>
        </div>

        <nav className="p-3 text-gray-800 dark:text-gray-200">
          <ul className="space-y-1">
            {showQtMenus && (
              <>
                <li>
                  <NavItem
                    to="/"
                    icon={<FiHome />}
                    label="Dashboard"
                    active={location.pathname === "/"}
                    onClick={closeMobileAfterClick}
                  />
                </li>

                <li>
                  <NavItem
                    to="/quotations"
                    icon={<FiFileText />}
                    label="Quotation Management"
                    active={location.pathname.startsWith("/quotations")}
                    onClick={closeMobileAfterClick}
                  />
                </li>

                <li>
                  <NavItem
                    to="/approvals"
                    icon={<FiCheckSquare />}
                    label="Approval Management"
                    active={location.pathname.startsWith("/approvals")}
                    onClick={closeMobileAfterClick}
                  />
                </li>
              </>
            )}

            <li>
              <button
                onClick={() => setIsTravelMenuOpen(!isTravelMenuOpen)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                aria-expanded={isTravelMenuOpen}
              >
                <span className="flex items-center gap-3">
                  <FiMapPin className="text-[18px]" />
                  <span className="text-sm font-medium">Travel Expense</span>
                </span>
                <FiChevronDown
                  className={`transition-transform ${
                    isTravelMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isTravelMenuOpen && (
                <ul className="mt-1 ml-9 space-y-1">
                  <li>
                    <NavItem
                      to="/travel-expense"
                      icon={<FiMapPin />}
                      label="Dashboard"
                      active={location.pathname === "/travel-expense"}
                      onClick={closeMobileAfterClick}
                    />
                  </li>
                  {canCreateTravelExpense() && (
                    <li>
                      <NavItem
                        to="/travel-expense/request"
                        icon={<FiFileText />}
                        label="Create Request"
                        active={location.pathname.startsWith(
                          "/travel-expense/request"
                        )}
                        onClick={closeMobileAfterClick}
                      />
                    </li>
                  )}
                  {showTravelApproveMenu && (
                    <li>
                      <NavItem
                        to="/travel-expense/approve"
                        icon={<FiCheckSquare />}
                        label="Approve"
                        active={location.pathname.startsWith(
                          "/travel-expense/approve"
                        )}
                        onClick={closeMobileAfterClick}
                      />
                    </li>
                  )}
                  {isAdmin() && (
                    <li>
                      <NavItem
                        to="/travel-expense/logs"
                        icon={<FiActivity />}
                        label="Logs"
                        active={location.pathname.startsWith(
                          "/travel-expense/logs"
                        )}
                        onClick={closeMobileAfterClick}
                      />
                    </li>
                  )}
                </ul>
              )}
            </li>

            {showQtMenus && isAdmin() && (
              <>
                <li>
                  <button
                    onClick={() => setIsFlowMenuOpen(!isFlowMenuOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                    aria-expanded={isFlowMenuOpen}
                  >
                    <span className="flex items-center gap-3">
                      <FiGitBranch className="text-[18px]" />
                      <span className="text-sm font-medium">
                        Flow Management
                      </span>
                    </span>
                    <FiChevronDown
                      className={`transition-transform ${
                        isFlowMenuOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isFlowMenuOpen && (
                    <ul className="mt-1 ml-9 space-y-1">
                      <li>
                        <NavItem
                          to="/approve-flow"
                          icon={<FiGitBranch />}
                          label="Create Flow"
                          active={location.pathname.startsWith("/approve-flow")}
                          onClick={closeMobileAfterClick}
                        />
                      </li>
                      <li>
                        <NavItem
                          to="/manage-flows"
                          icon={<FiGitBranch />}
                          label="Manage Flows"
                          active={location.pathname.startsWith("/manage-flows")}
                          onClick={closeMobileAfterClick}
                        />
                      </li>
                      <li>
                        <NavItem
                          to="/repair-approval-flow"
                          icon={<FiRefreshCw />}
                          label="Repair QT Flow"
                          active={location.pathname.startsWith(
                            "/repair-approval-flow"
                          )}
                          onClick={closeMobileAfterClick}
                        />
                      </li>
                      <li>
                        <NavItem
                          to="/create-team"
                          icon={<FiGitBranch />}
                          label="Create Team/TeamGroup"
                          active={location.pathname.startsWith("/create-team")}
                          onClick={closeMobileAfterClick}
                        />
                      </li>
                    </ul>
                  )}
                </li>

                <li>
                  <NavItem
                    to="/announcement-management"
                    icon={<FiBell />}
                    label="Announcement Management"
                    active={location.pathname.startsWith(
                      "/announcement-management"
                    )}
                    onClick={closeMobileAfterClick}
                  />
                </li>

                <li>
                  <NavItem
                    to="/users"
                    icon={<FiUsers />}
                    label="User Management"
                    active={location.pathname.startsWith("/users")}
                    onClick={closeMobileAfterClick}
                  />
                </li>

                <li>
                  <NavItem
                    to="/clients"
                    icon={<FiBriefcase />}
                    label="Client Management"
                    active={location.pathname.startsWith("/clients")}
                    onClick={closeMobileAfterClick}
                  />
                </li>

                <li>
                  <NavItem
                    to="/logs"
                    icon={<FiActivity />}
                    label="Logs"
                    active={location.pathname.startsWith("/logs")}
                    onClick={closeMobileAfterClick}
                  />
                </li>
              </>
            )}

            <li className="pt-1">
              <NavItem
                to="/profile"
                icon={<FiUser />}
                label="Profile"
                active={location.pathname.startsWith("/profile")}
                onClick={closeMobileAfterClick}
              />
            </li>

            <li>
              <button
                type="button"
                onClick={() => setIsTimesheetMenuOpen(!isTimesheetMenuOpen)}
                className={[
                  "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors",
                  location.pathname.startsWith("/timesheets")
                    ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                    : "text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-300",
                ].join(" ")}
                aria-expanded={isTimesheetMenuOpen}
              >
                <span className="flex items-center gap-3">
                  <FiClock className="text-[18px]" />
                  <span className="text-sm font-medium">Timesheet</span>
                </span>
                <FiChevronDown
                  className={`transition-transform ${
                    isTimesheetMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isTimesheetMenuOpen && (
                <ul className="mt-1 ml-9 space-y-1">
                  <li>
                    <NavItem
                      to="/timesheets"
                      icon={<FiClock />}
                      label="เวลาทำงานของฉัน"
                      active={location.pathname === "/timesheets"}
                      onClick={closeMobileAfterClick}
                    />
                  </li>
                  {timesheetCapabilities.canViewDashboard && (
                    <li>
                      <NavItem
                        to="/timesheets/dashboard"
                        icon={<FiBarChart2 />}
                        label="ภาพรวมทีม"
                        active={location.pathname.startsWith(
                          "/timesheets/dashboard"
                        )}
                        onClick={closeMobileAfterClick}
                      />
                    </li>
                  )}
                  {showTimesheetApprovalMenu && (
                    <li>
                      <NavItem
                        to={
                          timesheetCapabilities.canApproveTimesheet
                            ? "/timesheets/approvals"
                            : "/timesheets/approvals?tab=approved"
                        }
                        icon={<FiCheckSquare />}
                        label="อนุมัติ Timesheet"
                        active={location.pathname.startsWith(
                          "/timesheets/approvals"
                        )}
                        onClick={closeMobileAfterClick}
                      />
                    </li>
                  )}
                  {isAdmin() && (
                    <li>
                      <NavItem
                        to="/timesheets/reopen"
                        icon={<FiUnlock />}
                        label="เปิด Timesheet ย้อนหลัง"
                        active={location.pathname.startsWith(
                          "/timesheets/reopen"
                        )}
                        onClick={closeMobileAfterClick}
                      />
                    </li>
                  )}
                </ul>
              )}
            </li>

            <li>
              <NavItem
                to="/meeting-rooms"
                icon={<FiCalendar />}
                label="Meeting Room Booking"
                active={location.pathname.startsWith("/meeting-rooms")}
                onClick={closeMobileAfterClick}
              />
            </li>
          </ul>
        </nav>

        <div className="mt-auto px-4 py-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800">
          OPTX FI • v2.3.7
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

